<template>
  <div class="crawl-detail-popup" :class="{ 'embedded-mode': embedded }">
    <div v-if="!embedded" class="popup-header">
      <v-icon class="mr-2" color="white">mdi-hospital-building</v-icon>
      <div class="header-title">
        <span class="facility-name">{{ detail?.main?.cc_name || '시설 정보' }}</span>
        <template v-if="detail?.main?.cc_biz_gubun">
          <v-chip 
            v-for="(chip, ci) in parseBizGubun(detail.main.cc_biz_gubun)" 
            :key="ci" 
            size="x-small" 
            variant="flat" 
            :color="chip.color"
          >{{ chip.short }}</v-chip>
        </template>
      </div>
    </div>

    <div v-if="loading" class="popup-loading">
      <v-progress-circular indeterminate color="primary" size="48" />
      <p>데이터를 불러오는 중...</p>
    </div>

    <template v-else-if="detail">
      <!-- 탭 -->
      <v-tabs v-model="detailTab" :color="embedded ? 'primary' : 'white'" :bg-color="embedded ? 'white' : 'primary'" density="compact" :class="{ 'embedded-tabs': embedded }">
        <v-tab v-for="tab in detailTabs" :key="tab.value" :value="tab.value">
          <v-icon size="16" class="mr-1">{{ tab.icon }}</v-icon>
          {{ tab.label }}
        </v-tab>
      </v-tabs>

      <div class="popup-content" :class="{ 'embedded-content': embedded }">
        <v-window v-model="detailTab">

          <!-- ① 기본정보 -->
          <v-window-item value="basic">
            <div class="ltc-info-title">
              <v-icon size="18" class="mr-1">mdi-information-outline</v-icon>
              기본정보
            </div>
            <table class="ltc-info-table">
              <tbody>
                <tr><th>시설명</th><td colspan="3">{{ detail.main?.cc_name || '-' }}({{ detail.main?.cc_number }})</td></tr>
                <tr>
                  <th>사업구분</th>
                  <td colspan="3">
                    <template v-if="detail.main?.cc_biz_gubun">
                      <v-chip v-for="(chip, ci) in parseBizGubun(detail.main.cc_biz_gubun)" :key="ci" size="small" variant="flat" :color="chip.color" class="mr-1 mb-1">{{ chip.short }}</v-chip>
                    </template>
                    <span v-else>-</span>
                  </td>
                </tr>
                <tr><th>소재지</th><td colspan="3">{{ detail.main?.cc_addr || '-' }}</td></tr>
                <tr><th>전화번호</th><td>{{ detail.main?.cc_tell || '-' }}</td><th>팩스번호</th><td>{{ detail.main?.cc_fax || '-' }}</td></tr>
                <tr><th>이메일</th><td>{{ detail.main?.cc_email || '-' }}</td><th>홈페이지</th><td><a v-if="detail.main?.cc_web" :href="detail.main.cc_web" target="_blank" style="color:#1565c0;">{{ detail.main.cc_web }}</a><span v-else>-</span></td></tr>
                <tr><th>지정일자</th><td>{{ formatDate(detail.main?.cc_since) }}</td><th>설치신고일자</th><td>{{ formatDate(detail.main?.cc_registered) }}</td></tr>
                <tr><th>운영시간</th><td>{{ detail.main?.cc_hours || '-' }}</td><th>주차시설</th><td>{{ detail.main?.cc_parking || '-' }}</td></tr>
                <tr><th>교통편</th><td colspan="3">{{ detail.main?.cc_transport || '-' }}</td></tr>
                <tr><th>통합재가급여</th><td>{{ detail.main?.cc_integrated === 'Y' ? '제공' : '미제공' }}</td><th>통합재가 유형</th><td>{{ detail.main?.cc_integrated_type || '-' }}</td></tr>
                <tr><th>전문인배상책임보험</th><td>{{ detail.main?.cc_insurance_pro || '-' }}</td><th>손해배상책임보험</th><td>{{ detail.main?.cc_insurance_dmg || '-' }}</td></tr>
              </tbody>
            </table>
          </v-window-item>

          <!-- ② 평가정보 -->
          <v-window-item value="eval">
            <div class="ltc-info-title"><v-icon size="18" class="mr-1">mdi-clipboard-check-outline</v-icon>평가정보</div>
            <table class="ltc-info-table">
              <tbody>
                <tr><th>평가 급여종류</th><td colspan="3">{{ detail.main?.eval_type || '-' }}</td></tr>
                <tr><th>종합등급</th><td colspan="3"><v-chip v-if="detail.main?.eval_grade" variant="flat" color="primary" size="small">{{ detail.main.eval_grade }}</v-chip><span v-else>-</span></td></tr>
              </tbody>
            </table>
            <div class="ltc-info-title" style="margin-top:20px;"><v-icon size="18" class="mr-1">mdi-chart-bar</v-icon>영역별 등급</div>
            <table class="ltc-data-table">
              <thead><tr><th>기관운영</th><th>환경 및 안전</th><th>수급자 권리보장</th><th>급여제공과정</th><th>급여제공결과</th></tr></thead>
              <tbody><tr><td class="ltc-cell-center">{{ detail.main?.eval_management || '-' }}</td><td class="ltc-cell-center">{{ detail.main?.eval_safety || '-' }}</td><td class="ltc-cell-center">{{ detail.main?.eval_rights || '-' }}</td><td class="ltc-cell-center">{{ detail.main?.eval_process || '-' }}</td><td class="ltc-cell-center">{{ detail.main?.eval_result || '-' }}</td></tr></tbody>
            </table>
          </v-window-item>

          <!-- ③ 정원/현원 -->
          <v-window-item value="capacity">
            <div class="ltc-info-title"><v-icon size="18" class="mr-1">mdi-account-multiple-outline</v-icon>정원/현원 정보</div>
            <table class="ltc-data-table" v-if="detail.capacity?.length > 0">
              <thead>
                <tr>
                  <th rowspan="2">급여종류</th>
                  <th rowspan="2">정원</th>
                  <th colspan="2">현원</th>
                  <th colspan="2">대기자</th>
                  <th rowspan="2">이용가능</th>
                </tr>
                <tr>
                  <th>남</th>
                  <th>여</th>
                  <th>남</th>
                  <th>여</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, i) in detail.capacity" :key="i">
                  <td>{{ row.cap_service || '-' }}</td>
                  <td class="ltc-cell-center">{{ row.cap_capacity || 0 }}명</td>
                  <td class="ltc-cell-center">{{ row.cap_current_male || 0 }}명</td>
                  <td class="ltc-cell-center">{{ row.cap_current_female || 0 }}명</td>
                  <td class="ltc-cell-center">{{ row.cap_male_wait || 0 }}명</td>
                  <td class="ltc-cell-center">{{ row.cap_female_wait || 0 }}명</td>
                  <td class="ltc-cell-center">{{ row.cap_available || 0 }}명</td>
                </tr>
              </tbody>
            </table>
            <div v-else class="ltc-no-data-msg">등록된 정원/현원 정보가 없습니다.</div>
          </v-window-item>

          <!-- ④ 인력현황 -->
          <v-window-item value="staff">
            <div class="ltc-info-title">
              <v-icon size="18" class="mr-1">mdi-account-group-outline</v-icon>인력현황
              <span v-if="detail.main?.staff_update_date" class="ltc-update-date">최종변경일: {{ detail.main.staff_update_date }}</span>
            </div>
            <div class="ltc-table-scroll">
              <table class="ltc-data-table ltc-staff-table">
                <thead>
                  <tr>
                    <th rowspan="2">총인원</th>
                    <th rowspan="2">시설장</th>
                    <th rowspan="2">사무<br>국장</th>
                    <th rowspan="2">사회<br>복지사</th>
                    <th colspan="2">의사</th>
                    <th colspan="3">간호요원</th>
                    <th colspan="3">요양보호사</th>
                    <th rowspan="2">물리<br>치료사</th>
                    <th rowspan="2">작업<br>치료사</th>
                    <th rowspan="2">사무원</th>
                    <th rowspan="2">영양사</th>
                    <th rowspan="2">조리원</th>
                    <th rowspan="2">위생원</th>
                    <th rowspan="2">관리인</th>
                    <th rowspan="2">보조원</th>
                    <th rowspan="2">기타</th>
                  </tr>
                  <tr>
                    <th>전임</th>
                    <th>계약</th>
                    <th>간호사</th>
                    <th>조무사</th>
                    <th>치위생사</th>
                    <th>1급</th>
                    <th>2급</th>
                    <th>유예</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td class="ltc-cell-center ltc-cell-highlight">{{ detail.main?.staff_total || 0 }}</td>
                    <td class="ltc-cell-center">{{ detail.main?.staff_director || 0 }}</td>
                    <td class="ltc-cell-center">{{ detail.main?.staff_deputy || 0 }}</td>
                    <td class="ltc-cell-center">{{ detail.main?.staff_social_worker || 0 }}</td>
                    <td class="ltc-cell-center">{{ detail.main?.staff_doctor_fulltime || 0 }}</td>
                    <td class="ltc-cell-center">{{ detail.main?.staff_doctor_contract || 0 }}</td>
                    <td class="ltc-cell-center">{{ detail.main?.staff_nurse || 0 }}</td>
                    <td class="ltc-cell-center">{{ detail.main?.staff_nurse_aid || 0 }}</td>
                    <td class="ltc-cell-center">{{ detail.main?.staff_dental_hyg || 0 }}</td>
                    <td class="ltc-cell-center">{{ detail.main?.staff_caregiver1 || 0 }}</td>
                    <td class="ltc-cell-center">{{ detail.main?.staff_caregiver2 || 0 }}</td>
                    <td class="ltc-cell-center">{{ detail.main?.staff_caregiver_grace || 0 }}</td>
                    <td class="ltc-cell-center">{{ detail.main?.staff_pt || 0 }}</td>
                    <td class="ltc-cell-center">{{ detail.main?.staff_ot || 0 }}</td>
                    <td class="ltc-cell-center">{{ detail.main?.staff_clerk || 0 }}</td>
                    <td class="ltc-cell-center">{{ detail.main?.staff_dietitian || 0 }}</td>
                    <td class="ltc-cell-center">{{ detail.main?.staff_cook || 0 }}</td>
                    <td class="ltc-cell-center">{{ detail.main?.staff_janitor || 0 }}</td>
                    <td class="ltc-cell-center">{{ detail.main?.staff_guard || 0 }}</td>
                    <td class="ltc-cell-center">{{ detail.main?.staff_assistant || 0 }}</td>
                    <td class="ltc-cell-center">{{ detail.main?.staff_other || 0 }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="ltc-info-title" style="margin-top:24px;">
              <v-icon size="18" class="mr-1">mdi-clock-outline</v-icon>근속현황
              <span class="ltc-info-note">※ 요양보호사(방문목욕, 방문요양, 방문간호)는 배상보험에 가입된 기준으로 조회됩니다.</span>
            </div>
            <table class="ltc-data-table" v-if="detail.tenure?.length > 0">
              <thead>
                <tr>
                  <th>장기요양요원</th>
                  <th>합계</th>
                  <th>3개월미만</th>
                  <th>3개월이상<br>6개월미만</th>
                  <th>6개월이상<br>1년미만</th>
                  <th>1년이상<br>2년미만</th>
                  <th>2년이상</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, i) in detail.tenure" :key="i">
                  <td>{{ row.st_position || '-' }}</td>
                  <td class="ltc-cell-center ltc-cell-highlight">{{ row.st_total }}</td>
                  <td class="ltc-cell-center">{{ row.st_under_3m }}</td>
                  <td class="ltc-cell-center">{{ row.st_3m_6m }}</td>
                  <td class="ltc-cell-center">{{ row.st_6m_1y }}</td>
                  <td class="ltc-cell-center">{{ row.st_1y_2y }}</td>
                  <td class="ltc-cell-center">{{ row.st_over_2y }}</td>
                </tr>
              </tbody>
            </table>
            <div v-else class="ltc-no-data-msg">등록된 근속현황 정보가 없습니다.</div>
          </v-window-item>

          <!-- ⑤ 시설현황 -->
          <v-window-item value="facility">
            <div class="ltc-info-title"><v-icon size="18" class="mr-1">mdi-home-city-outline</v-icon>시설현황</div>
            <!-- 재가시설(주야간보호) 유형 테이블 -->
            <table class="ltc-data-table" v-if="hasDayCareData">
              <thead>
                <tr>
                  <th>급여종류</th>
                  <th>1인 생활실</th>
                  <th>프로그램실</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, i) in detail.facility" :key="'dc-'+i" v-show="row.fac_single_room > 0 || row.fac_program_room > 0 || (!row.fac_room_total && !row.fac_room_1)">
                  <td>{{ row.fac_service || '-' }}</td>
                  <td class="ltc-cell-center">{{ row.fac_single_room || 0 }}</td>
                  <td class="ltc-cell-center">{{ row.fac_program_room || 0 }}</td>
                </tr>
              </tbody>
            </table>
            <!-- 요양시설 유형 테이블 -->
            <table class="ltc-data-table" v-if="hasNursingHomeData" style="margin-top:12px;">
              <thead>
                <tr>
                  <th>급여형태</th>
                  <th>침실합계</th>
                  <th>1인실</th>
                  <th>2인실</th>
                  <th>3인실</th>
                  <th>4인실</th>
                  <th>특실</th>
                  <th>기타</th>
                  <th>공동거실</th>
                  <th>화장실</th>
                  <th>욕실</th>
                  <th>옥외공간</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, i) in detail.facility" :key="'nh-'+i" v-show="row.fac_room_total > 0 || row.fac_room_1 > 0">
                  <td>{{ row.fac_service || '-' }}</td>
                  <td class="ltc-cell-center ltc-cell-highlight">{{ row.fac_room_total || 0 }}</td>
                  <td class="ltc-cell-center">{{ row.fac_room_1 || 0 }}</td>
                  <td class="ltc-cell-center">{{ row.fac_room_2 || 0 }}</td>
                  <td class="ltc-cell-center">{{ row.fac_room_3 || 0 }}</td>
                  <td class="ltc-cell-center">{{ row.fac_room_4 || 0 }}</td>
                  <td class="ltc-cell-center">{{ row.fac_room_special || 0 }}</td>
                  <td class="ltc-cell-center">{{ row.fac_room_etc || 0 }}</td>
                  <td class="ltc-cell-center">{{ row.fac_living || 0 }}</td>
                  <td class="ltc-cell-center">{{ row.fac_toilet || 0 }}</td>
                  <td class="ltc-cell-center">{{ row.fac_bath || 0 }}</td>
                  <td class="ltc-cell-center">{{ row.fac_outdoor || 0 }}</td>
                </tr>
              </tbody>
            </table>
            <div v-if="!detail.facility?.length" class="ltc-no-data-msg">등록된 시설현황 정보가 없습니다.</div>
            <div class="ltc-info-title" style="margin-top:24px;"><v-icon size="18" class="mr-1">mdi-car-outline</v-icon>방문목욕 차량현황</div>
            <table class="ltc-data-table" v-if="detail.vehicle?.length > 0">
              <thead><tr><th>차량대수</th><th>차량번호</th></tr></thead>
              <tbody><tr v-for="(row, i) in detail.vehicle" :key="i"><td class="ltc-cell-center">{{ row.veh_count }}</td><td>{{ row.veh_number || '-' }}</td></tr></tbody>
            </table>
            <div v-else class="ltc-no-data-msg">등록된 차량 정보가 없습니다.</div>
            <div class="ltc-info-title" style="margin-top:24px;"><v-icon size="18" class="mr-1">mdi-medical-bag</v-icon>복지용구 현황</div>
            <table class="ltc-data-table" v-if="detail.equipment?.length > 0">
              <thead><tr><th>신고번호</th><th>품목명</th><th>제조사</th><th>모델명</th><th>용도</th><th>비고</th></tr></thead>
              <tbody><tr v-for="(row, i) in detail.equipment" :key="i"><td>{{ row.eq_report || '-' }}</td><td>{{ row.eq_name || '-' }}</td><td>{{ row.eq_manufacturer || '-' }}</td><td>{{ row.eq_model || '-' }}</td><td>{{ row.eq_purpose || '-' }}</td><td>{{ row.eq_etc || '-' }}</td></tr></tbody>
            </table>
            <div v-else class="ltc-no-data-msg">등록된 복지용구 정보가 없습니다.</div>
          </v-window-item>

          <!-- ⑥ 비급여비용 -->
          <v-window-item value="cost">
            <div class="ltc-info-title"><v-icon size="18" class="mr-1">mdi-currency-krw</v-icon>비급여 항목별 비용</div>
            <table class="ltc-data-table" v-if="detail.cost?.length > 0">
              <thead><tr><th>항목</th><th>산출근거</th><th>1일 비용</th><th>월(31일) 비용</th><th>비고</th><th>등록일</th></tr></thead>
              <tbody><tr v-for="(row, i) in detail.cost" :key="i"><td>{{ row.cost_item || '-' }}</td><td>{{ row.cost_basis || '-' }}</td><td class="ltc-cell-right">{{ formatCurrency(row.cost_daily) }}</td><td class="ltc-cell-right">{{ formatCurrency(row.cost_monthly) }}</td><td>{{ row.cost_note || '-' }}</td><td class="ltc-cell-center">{{ formatDate(row.cost_reg_date) }}</td></tr></tbody>
            </table>
            <div v-else class="ltc-no-data-msg">등록된 비급여비용 정보가 없습니다.</div>
          </v-window-item>

          <!-- ⑦ 프로그램 -->
          <v-window-item value="program">
            <div class="ltc-info-title"><v-icon size="18" class="mr-1">mdi-calendar-star</v-icon>프로그램 운영 현황</div>
            <table class="ltc-data-table" v-if="detail.program?.length > 0">
              <thead><tr><th>종류</th><th>프로그램명</th><th>대상인원</th><th>진행주기</th><th>장소</th></tr></thead>
              <tbody><tr v-for="(row, i) in detail.program" :key="i"><td>{{ row.prog_category || '-' }}</td><td>{{ row.prog_name || '-' }}</td><td class="ltc-cell-center">{{ row.prog_target }}</td><td>{{ row.prog_cycle || '-' }}</td><td>{{ row.prog_location || '-' }}</td></tr></tbody>
            </table>
            <div v-else class="ltc-no-data-msg">등록된 프로그램 정보가 없습니다.</div>
          </v-window-item>

          <!-- ⑧ 영업관리 -->
          <v-window-item value="sales">
            <!-- 활성 영업이 없는 경우 -->
            <div v-if="!salesInfo" class="sales-empty-state">
              <v-icon size="64" color="grey-lighten-1">mdi-handshake-outline</v-icon>
              <p v-if="salesLogs.length === 0">아직 영업이 시작되지 않은 시설입니다.</p>
              <p v-else>현재 진행 중인 영업이 없습니다.</p>
              <p class="sales-empty-hint">아래 '영업시작' 버튼을 클릭하여 이 시설에 대한 영업을 시작하세요.</p>
            </div>

            <!-- 활성 영업이 있는 경우: 현황 테이블 -->
            <template v-if="salesInfo">
              <table class="ltc-info-table sales-info-table">
                <tbody>
                  <tr v-if="canSeeSalesDetail">
                    <th>담당 조직</th>
                    <td>{{ salesInfo.sales_org_name }}</td>
                    <th>담당자</th>
                    <td>{{ salesInfo.sales_user_name || '-' }}</td>
                  </tr>
                  <tr>
                    <th>영업 시작일</th>
                    <td>{{ formatDate(salesInfo.sales_wdate) }}</td>
                    <th>상태</th>
                    <td>
                      <v-chip 
                        size="small" 
                        :color="getSalesStatusColor(salesInfo.sales_status)"
                        variant="flat"
                      >
                        <v-icon size="14" class="mr-1">{{ getSalesStatusIcon(salesInfo.sales_status) }}</v-icon>
                        {{ getSalesStatusLabel(salesInfo.sales_status) }}
                      </v-chip>
                    </td>
                  </tr>
                  <tr>
                    <th>메모</th>
                    <td colspan="3">
                      <v-textarea
                        v-model="salesInfo.sales_note"
                        placeholder="영업 관련 메모를 입력하세요..."
                        rows="3"
                        variant="outlined"
                        density="compact"
                        hide-details
                        :disabled="!hasPermission"
                        @blur="updateSalesNote"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>

              <!-- 영업 액션 버튼 (영업중일 때만) -->
              <div v-if="hasPermission && salesInfo.sales_status === '영업중'" class="sales-action-zone">
                <v-btn 
                  variant="flat" 
                  class="btn-save"
                  size="small"
                  @click="convertSales"
                  :loading="salesLoading"
                >
                  <v-icon size="16" class="mr-1">mdi-account-check-outline</v-icon>
                  회원가입
                </v-btn>
                <v-btn 
                  variant="flat" 
                  class="btn-stop"
                  size="small"
                  @click="openStopDialog"
                >
                  <v-icon size="16" class="mr-1">mdi-pause-circle-outline</v-icon>
                  영업 중단
                </v-btn>
              </div>
            </template>

            <!-- 영업 이력 로그 (활성 영업 유무와 관계없이 항상 표시) -->
            <div v-if="salesLogs.length > 0" class="sales-log-section">
              <div class="ltc-info-title">
                <v-icon size="18" class="mr-1">mdi-history</v-icon>
                영업 이력 ({{ salesLogs.length }}건)
              </div>
              <div class="sales-log-list">
                <div 
                  v-for="log in salesLogs" 
                  :key="log.log_idx" 
                  class="sales-log-item"
                  :class="getLogTypeClass(log.log_type)"
                >
                  <div class="log-icon">
                    <v-icon size="14" :color="getLogTypeColor(log.log_type)">{{ getLogTypeIcon(log.log_type) }}</v-icon>
                  </div>
                  <div class="log-body">
                    <!-- 수정 모드 -->
                    <template v-if="editingLogIdx === log.log_idx">
                      <v-textarea
                        v-model="editingLogContent"
                        rows="2"
                        variant="outlined"
                        density="compact"
                        hide-details
                        autofocus
                      />
                      <div class="log-edit-actions">
                        <v-btn variant="flat" class="btn-save" size="x-small" @click="saveLogEdit(log.log_idx)">저장</v-btn>
                        <v-btn variant="text" class="btn-cancel" size="x-small" @click="cancelLogEdit">취소</v-btn>
                      </div>
                    </template>
                    <!-- 일반 모드 -->
                    <template v-else>
                      <div class="log-header">
                        <v-chip 
                          size="x-small" 
                          :color="getLogTypeColor(log.log_type)" 
                          variant="flat"
                          class="log-type-chip"
                        >{{ getLogTypeLabel(log.log_type) }}</v-chip>
                        <span v-if="canSeeSalesDetail" class="log-org">{{ log.org_name }} · {{ log.u_name }}</span>
                      </div>
                      <div class="log-content">{{ log.log_content }}</div>
                      <div class="log-meta">
                        <span class="log-date-label">시작</span>
                        <span class="log-date">{{ formatDateTime(log.log_wdate) }}</span>
                        <template v-if="log.log_end_date">
                          <span class="log-date-separator">→</span>
                          <span class="log-date-label">{{ getEndDateLabel(log.log_type) }}</span>
                          <span class="log-date">{{ formatDateTime(log.log_end_date) }}</span>
                          <span v-if="log.log_type !== 'start'" class="log-duration">({{ getDuration(log.log_wdate, log.log_end_date) }})</span>
                        </template>
                      </div>
                    </template>
                  </div>
                  <!-- 관리자 수정/삭제 버튼 -->
                  <div v-if="isAdmin && editingLogIdx !== log.log_idx" class="log-admin-actions">
                    <v-btn icon size="x-small" variant="text" @click="startLogEdit(log)">
                      <v-icon size="14" color="#607d8b">mdi-pencil-outline</v-icon>
                    </v-btn>
                    <v-btn icon size="x-small" variant="text" @click="confirmDeleteLog(log.log_idx)">
                      <v-icon size="14" color="#c62828">mdi-delete-outline</v-icon>
                    </v-btn>
                  </div>
                </div>
              </div>
            </div>
          </v-window-item>

        </v-window>
      </div>

      <div v-if="!embedded" class="popup-footer">
        <div class="ltc-detail-meta">
          <span v-if="detail.main?.cc_rdate">최근 갱신: {{ formatDateTime(detail.main.cc_rdate) }}</span>
          <span v-else-if="detail.main?.cc_wdate">최초 수집: {{ formatDateTime(detail.main.cc_wdate) }}</span>
        </div>
        <v-spacer />
        <!-- 영업시작 버튼 (활성 영업이 없을 때) -->
        <template v-if="!salesInfo && currentUser">
          <span v-if="salesLogs.length > 0" class="sales-history-hint" @click="detailTab = 'sales'">
            <v-icon size="14" class="mr-1">mdi-history</v-icon>
            영업이력 {{ salesLogs.length }}건
          </span>
          <v-btn 
            variant="flat" 
            class="btn-start"
            @click="startSales"
            :loading="salesLoading"
          >
            <v-icon size="18" class="mr-1">mdi-rocket-launch-outline</v-icon>
            영업시작
          </v-btn>
        </template>
        <!-- 영업 중일 때 상태 표시 (클릭 시 영업관리 탭 이동) -->
        <div v-else-if="salesInfo" class="sales-status-badge" @click="detailTab = 'sales'" style="cursor:pointer;">
          <v-chip 
            size="small" 
            :color="getSalesStatusColor(salesInfo.sales_status)"
            variant="flat"
          >
            <v-icon size="14" class="mr-1">{{ getSalesStatusIcon(salesInfo.sales_status) }}</v-icon>
            {{ getSalesStatusLabel(salesInfo.sales_status) }}
          </v-chip>
          <template v-if="canSeeSalesDetail">
            <span class="sales-org-name">{{ salesInfo.sales_org_name }}</span>
            <span v-if="salesInfo.sales_user_name" class="sales-user-name">{{ salesInfo.sales_user_name }}</span>
          </template>
        </div>
      </div>
    </template>

    <div v-else class="popup-error">
      <v-icon size="48" color="grey">mdi-alert-circle-outline</v-icon>
      <p>시설 정보를 찾을 수 없습니다.</p>
    </div>

    <!-- 영업 중단 사유 입력 다이얼로그 -->
    <v-dialog v-model="stopDialog" max-width="500">
      <v-card>
        <v-card-title class="stop-dialog-title">
          <span>영업 중단</span>
          <button
            type="button"
            class="stop-dialog__close"
            :disabled="salesLoading"
            aria-label="닫기"
            @click="stopDialog = false"
          >
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </v-card-title>
        <v-card-text>
          <p class="mb-3">영업 중단 사유를 입력해주세요.</p>
          <v-textarea
            v-model="stopReason"
            placeholder="중단 사유를 상세히 작성해주세요..."
            rows="4"
            variant="outlined"
            density="compact"
            hide-details
            autofocus
          />
        </v-card-text>
        <v-card-actions>
          <v-btn variant="text" class="btn-cancel" @click="stopDialog = false">취소</v-btn>
          <v-spacer />
          <v-btn 
            variant="flat" 
            class="btn-stop" 
            @click="stopSales"
            :disabled="!stopReason.trim()"
            :loading="salesLoading"
          >중단</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 회원가입 ID/PW 설정 다이얼로그 -->
    <v-dialog v-model="convertDialog" max-width="500" persistent>
      <v-card>
        <v-card-title class="convert-dialog-title">
          <v-icon class="mr-2" color="primary">mdi-account-plus</v-icon>
          회원 ID/PW 설정
          <button
            type="button"
            class="convert-dialog__close"
            :disabled="salesLoading"
            aria-label="닫기"
            @click="cancelConvert"
          >
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </v-card-title>
        <v-card-text>
          <p class="convert-dialog-desc">고객들이 사용할 ID와 비밀번호를 설정해주세요.</p>
          <div class="convert-form">
            <div class="form-group">
              <label class="form-label">아이디 (ID) <span class="required">*</span></label>
              <div class="id-input-row">
                <input 
                  v-model="convertForm.memberId" 
                  type="text" 
                  class="native-input"
                  placeholder="아이디를 입력하세요"
                  :class="{ 'is-valid': convertForm.idChecked && convertForm.idAvailable, 'is-invalid': convertForm.idChecked && !convertForm.idAvailable }"
                  @input="onIdInputChange"
                />
                <button 
                  type="button" 
                  class="check-btn" 
                  @click="checkIdDuplicate"
                  :disabled="!convertForm.memberId.trim() || idCheckLoading"
                >
                  <v-progress-circular v-if="idCheckLoading" indeterminate size="14" width="2" />
                  <template v-else>중복확인</template>
                </button>
              </div>
              <span v-if="convertForm.idChecked && convertForm.idAvailable" class="form-hint success">
                <v-icon size="12" class="mr-1">mdi-check-circle</v-icon>
                사용 가능한 아이디입니다.
              </span>
              <span v-else-if="convertForm.idChecked && !convertForm.idAvailable" class="form-hint error">
                <v-icon size="12" class="mr-1">mdi-alert-circle</v-icon>
                이미 사용 중인 아이디입니다.
              </span>
              <span v-else class="form-hint">기본값: 기관기호 ({{ detail?.main?.cc_number }})</span>
            </div>
            <div class="form-group">
              <label class="form-label">비밀번호 (PW)</label>
              <input 
                v-model="convertForm.memberPw" 
                type="text" 
                class="native-input"
                placeholder="비밀번호를 입력하세요"
              />
              <span class="form-hint">기본값: 기관기호 ({{ detail?.main?.cc_number }})</span>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn variant="text" class="btn-cancel" @click="cancelConvert">취소</v-btn>
          <v-spacer />
          <v-btn 
            variant="flat" 
            class="btn-save" 
            @click="doConvertSales"
            :loading="salesLoading"
            :disabled="!convertForm.idChecked || !convertForm.idAvailable"
          >
            <v-icon size="16" class="mr-1">mdi-check</v-icon>
            확인
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 스낵바 -->
    <v-snackbar v-model="snackbar" :timeout="2000" color="success" location="bottom">
      {{ snackbarText }}
    </v-snackbar>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { crawlApi, salesApi, orgApi, authApi } from '@/services/api';

// Props (embedded 모드 지원)
const props = defineProps<{
  ccNumber?: string;
  embedded?: boolean;
}>();

const route = useRoute();
const store = useStore();

const loading = ref(true);
const salesLoading = ref(false);
const detail = ref<any>(null);
const detailTab = ref('basic');
const bizGubunMap = ref<any[]>([]);

// 영업 관련
const salesInfo = ref<any>(null);
const salesLogs = ref<any[]>([]);
const stopDialog = ref(false);
const stopReason = ref('');

// 회원가입 ID/PW 설정 다이얼로그
const convertDialog = ref(false);
const idCheckLoading = ref(false);
const convertForm = ref({
  memberId: '',
  memberPw: '',
  idChecked: false,
  idAvailable: false
});

// 로그 수정/삭제 (관리자)
const editingLogIdx = ref<number | null>(null);
const editingLogContent = ref('');

// 스낵바
const snackbar = ref(false);
const snackbarText = ref('');

// 현재 사용자 정보
const currentUser = computed(() => store.getters.currentUser);

// 영업 가시성: 관리자(60+) 또는 장(60,70)이 볼 수 있는 하위 조직 코드 Set
const visibleOrgCodes = ref<Set<string>>(new Set());

// 담당 조직/담당자 정보를 볼 수 있는지 확인
const canSeeSalesDetail = computed(() => {
  if (!salesInfo.value || !currentUser.value) return false;
  if (currentUser.value.roleLevel >= 60) return true;
  if (currentUser.value.roleLevel >= 60) {
    return visibleOrgCodes.value.has(salesInfo.value.org_code);
  }
  return false;
});

// 조직 트리에서 특정 org_code의 모든 하위 조직 코드를 재귀적으로 수집
const collectDescendantCodes = (tree: any[], orgCode: string): string[] => {
  const codes: string[] = [];
  const findAndCollect = (nodes: any[], found: boolean) => {
    for (const node of nodes) {
      if (found || node.org_code === orgCode) {
        codes.push(node.org_code);
        if (node.children) findAndCollect(node.children, true);
        if (!found && node.org_code === orgCode) continue;
      }
      if (!found && node.children) findAndCollect(node.children, false);
    }
  };
  findAndCollect(tree, false);
  return codes;
};

const loadVisibleOrgCodes = async () => {
  if (!currentUser.value) return;
  if (currentUser.value.roleLevel >= 60 && currentUser.value.roleLevel < 60) {
    try {
      const res = await orgApi.getTree();
      const tree = res.data?.data || [];
      const codes = collectDescendantCodes(tree, currentUser.value.orgCode);
      visibleOrgCodes.value = new Set(codes);
    } catch (e) {
      console.error('조직 트리 로드 오류:', e);
    }
  }
};

// 부모 창에 영업 변경 알림 전송
const notifyParentSalesUpdate = () => {
  const ccNumber = detail.value?.main?.cc_number;
  if (!ccNumber) return;
  try {
    if (window.opener && !window.opener.closed) {
      window.opener.postMessage({
        type: 'salesUpdated',
        ccNumber,
        salesInfo: salesInfo.value ? {
          sales_idx: salesInfo.value.sales_idx,
          sales_status: salesInfo.value.sales_status,
          sales_org_name: salesInfo.value.sales_org_name,
          sales_user_name: salesInfo.value.sales_user_name,
          org_code: salesInfo.value.org_code
        } : null
      }, '*');
    }
  } catch (e) {
    // cross-origin 등 무시
  }
};

// 영업 권한 확인 (영업 시작 당사자 OR 관리자 roleLevel 60+)
const hasPermission = computed(() => {
  if (!salesInfo.value || !currentUser.value) return false;
  // 관리자 (roleLevel 60+)는 항상 권한 있음
  if (currentUser.value.roleLevel >= 60) return true;
  // 영업 시작한 당사자만 권한 있음
  return salesInfo.value.u_idx === currentUser.value.idx;
});

const detailTabs = computed(() => {
  const tabs = [
    { value: 'basic', label: '기본정보', icon: 'mdi-information-outline' },
    { value: 'eval', label: '평가정보', icon: 'mdi-clipboard-check-outline' },
    { value: 'capacity', label: '정원/현원', icon: 'mdi-account-multiple-outline' },
    { value: 'staff', label: '인력현황', icon: 'mdi-account-group-outline' },
    { value: 'facility', label: '시설현황', icon: 'mdi-home-city-outline' },
    { value: 'cost', label: '비급여비용', icon: 'mdi-currency-krw' },
    { value: 'program', label: '프로그램', icon: 'mdi-calendar-star' }
  ];
  
  if (currentUser.value) {
    tabs.push({ value: 'sales', label: '영업관리', icon: 'mdi-handshake-outline' });
  }
  
  return tabs;
});

// 시설현황: 재가시설(주야간보호) 데이터 유무
const hasDayCareData = computed(() => {
  if (!detail.value?.facility?.length) return false;
  return detail.value.facility.some((row: any) => 
    (row.fac_single_room > 0 || row.fac_program_room > 0) || 
    (!row.fac_room_total && !row.fac_room_1)
  );
});

// 시설현황: 요양시설 데이터 유무
const hasNursingHomeData = computed(() => {
  if (!detail.value?.facility?.length) return false;
  return detail.value.facility.some((row: any) => 
    row.fac_room_total > 0 || row.fac_room_1 > 0
  );
});

// 영업 상태 색상/아이콘 (상태값이 한국어로 저장됨)
const getSalesStatusLabel = (status: string) => {
  const labels: Record<string, string> = {
    '영업중': '영업중',
    '회원가입': '회원가입성공'
  };
  return labels[status] || status || '-';
};

const getSalesStatusColor = (status: string) => {
  const colors: Record<string, string> = {
    '영업중': '#1565c0',
    '회원가입': '#2e7d32'
  };
  return colors[status] || '#78909c';
};

const getSalesStatusIcon = (status: string) => {
  const icons: Record<string, string> = {
    '영업중': 'mdi-briefcase-clock',
    '회원가입': 'mdi-check-circle-outline'
  };
  return icons[status] || 'mdi-circle';
};

// 로그 타입 아이콘/색상
const getLogTypeIcon = (type: string) => {
  const icons: Record<string, string> = {
    start: 'mdi-play-circle',
    stop: 'mdi-pause-circle',
    resume: 'mdi-play-circle',
    convert: 'mdi-account-check',
    withdraw: 'mdi-account-remove',
    hide: 'mdi-eye-off',
    unhide: 'mdi-eye'
  };
  return icons[type] || 'mdi-circle';
};

const getLogTypeColor = (type: string) => {
  const colors: Record<string, string> = {
    start: '#1565c0',
    stop: '#c62828',
    resume: '#2e7d32',
    convert: '#2e7d32',
    withdraw: '#ff6f00',
    hide: '#78909c',
    unhide: '#78909c'
  };
  return colors[type] || '#78909c';
};

const getLogTypeLabel = (type: string) => {
  const labels: Record<string, string> = {
    start: '영업중',
    stop: '영업중단',
    resume: '영업재개',
    convert: '회원가입',
    withdraw: '회원탈퇴',
    hide: '숨김',
    unhide: '공개'
  };
  return labels[type] || type;
};

const getLogTypeClass = (type: string) => {
  return {
    'is-start': type === 'start',
    'is-stop': type === 'stop',
    'is-convert': type === 'convert',
    'is-resume': type === 'resume',
    'is-withdraw': type === 'withdraw'
  };
};

const getEndDateLabel = (type: string) => {
  const labels: Record<string, string> = {
    stop: '중단',
    convert: '가입완료',
    withdraw: '탈퇴',
    start: '종료'
  };
  return labels[type] || '종료';
};

const getDuration = (startDate: string, endDate: string) => {
  if (!startDate || !endDate) return '';
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffMs = end.getTime() - start.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return '당일';
  if (diffDays < 30) return `${diffDays}일`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}개월`;
  return `${Math.floor(diffDays / 365)}년 ${Math.floor((diffDays % 365) / 30)}개월`;
};

// 사업구분 파싱
const parseBizGubun = (raw: string) => {
  if (!raw) return [];
  const items = raw.split(',').map(s => s.trim()).filter(Boolean);
  return items.map(item => {
    const match = bizGubunMap.value.find(m => m.cbm_raw?.split(',').some((r: string) => r.trim() === item));
    return { short: match?.cbm_short || item, color: match?.cbm_color || '#607d8b' };
  });
};

// 포맷 유틸
const formatDate = (value: string | null) => {
  if (!value) return '-';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const formatDateTime = (value: string | null) => {
  if (!value) return '-';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const formatCurrency = (value: number | null) => {
  if (!value && value !== 0) return '-';
  return value.toLocaleString() + '원';
};

// 영업 시작 (확인 후 진행)
const startSales = async () => {
  if (!currentUser.value || !detail.value?.main?.cc_number) return;
  
  const facilityName = detail.value.main.cc_name || '해당 시설';
  if (!confirm(`'${facilityName}' 시설의 영업을 시작하시겠습니까?`)) return;
  
  salesLoading.value = true;
  try {
    await salesApi.startSales({
      cc_number: detail.value.main.cc_number,
      org_code: currentUser.value.orgCode,
      org_name: currentUser.value.orgName || '',
      u_idx: currentUser.value.idx,
      u_name: currentUser.value.name || ''
    });
    
    await loadSalesInfo(detail.value.main.cc_number);
    notifyParentSalesUpdate();
    detailTab.value = 'sales';
  } catch (error: any) {
    console.error('영업 시작 오류:', error);
    alert(error.response?.data?.message || '영업 시작 중 오류가 발생했습니다.');
  } finally {
    salesLoading.value = false;
  }
};

// 영업 중단 다이얼로그 열기
const openStopDialog = () => {
  stopReason.value = '';
  stopDialog.value = true;
};

// 영업 중단 (확인 후 진행)
const stopSales = async () => {
  if (!salesInfo.value || !hasPermission.value || !stopReason.value.trim()) return;
  
  const reason = stopReason.value.trim();
  if (!confirm(`사유: "${reason}"\n\n해당 사유로 영업을 중단하시겠습니까?`)) return;
  
  salesLoading.value = true;
  try {
    await salesApi.stopSales(salesInfo.value.sales_idx, {
      stop_reason: reason,
      cc_number: detail.value.main.cc_number,
      org_code: currentUser.value.orgCode,
      org_name: currentUser.value.orgName || '',
      u_idx: currentUser.value.idx,
      u_name: currentUser.value.name || ''
    });
    
    stopDialog.value = false;
    stopReason.value = '';
    await loadSalesInfo(detail.value.main.cc_number);
    notifyParentSalesUpdate();
  } catch (error: any) {
    console.error('영업 중단 오류:', error);
    alert(error.response?.data?.message || '영업 중단 중 오류가 발생했습니다.');
  } finally {
    salesLoading.value = false;
  }
};

// 회원가입 (영업 성공 — 확인 후 ID/PW 설정 모달 표시)
const convertSales = async () => {
  if (!salesInfo.value || !hasPermission.value) return;
  
  const facilityName = detail.value.main?.cc_name || '해당 시설';
  if (!confirm(`'${facilityName}' 시설을 회원가입 시키겠습니까?`)) return;
  
  // 기관기호를 기본값으로 설정
  const ccNumber = detail.value.main?.cc_number || '';
  convertForm.value.memberId = ccNumber;
  convertForm.value.memberPw = ccNumber;
  convertForm.value.idChecked = false;
  convertForm.value.idAvailable = false;
  
  // ID/PW 설정 모달 표시
  convertDialog.value = true;
};

// 아이디 입력 변경 시 중복확인 상태 초기화
const onIdInputChange = () => {
  convertForm.value.idChecked = false;
  convertForm.value.idAvailable = false;
};

// 아이디 중복확인
const checkIdDuplicate = async () => {
  const memberId = convertForm.value.memberId.trim();
  if (!memberId) {
    alert('아이디를 입력해주세요.');
    return;
  }
  
  idCheckLoading.value = true;
  try {
    const response = await authApi.checkIdDuplicate(memberId, 'member');
    const isDuplicate = response.data?.data?.isDuplicate;
    
    convertForm.value.idChecked = true;
    convertForm.value.idAvailable = !isDuplicate;
  } catch (error: any) {
    console.error('ID 중복확인 오류:', error);
    alert('중복확인 중 오류가 발생했습니다.');
    convertForm.value.idChecked = false;
    convertForm.value.idAvailable = false;
  } finally {
    idCheckLoading.value = false;
  }
};

// 회원가입 취소
const cancelConvert = () => {
  convertDialog.value = false;
  convertForm.value.memberId = '';
  convertForm.value.memberPw = '';
  convertForm.value.idChecked = false;
  convertForm.value.idAvailable = false;
};

// 회원가입 실행 (ID/PW 설정 후)
const doConvertSales = async () => {
  if (!salesInfo.value || !hasPermission.value) return;
  
  if (!convertForm.value.memberId.trim()) {
    alert('아이디를 입력해주세요.');
    return;
  }
  
  if (!convertForm.value.idChecked || !convertForm.value.idAvailable) {
    alert('아이디 중복확인을 해주세요.');
    return;
  }
  
  if (!convertForm.value.memberPw.trim()) {
    alert('비밀번호를 입력해주세요.');
    return;
  }
  
  salesLoading.value = true;
  try {
    await salesApi.convertSales(salesInfo.value.sales_idx, {
      cc_number: detail.value.main.cc_number,
      org_code: currentUser.value.orgCode,
      org_name: currentUser.value.orgName || '',
      u_idx: currentUser.value.idx,
      u_name: currentUser.value.name || '',
      member_id: convertForm.value.memberId.trim(),
      member_pw: convertForm.value.memberPw.trim()
    });
    
    convertDialog.value = false;
    convertForm.value.memberId = '';
    convertForm.value.memberPw = '';
    convertForm.value.idChecked = false;
    convertForm.value.idAvailable = false;
    
    await loadSalesInfo(detail.value.main.cc_number);
    notifyParentSalesUpdate();
    snackbarText.value = '회원가입이 완료되었습니다.';
    snackbar.value = true;
  } catch (error: any) {
    console.error('회원가입 처리 오류:', error);
    alert(error.response?.data?.message || '회원가입 처리 중 오류가 발생했습니다.');
  } finally {
    salesLoading.value = false;
  }
};

// 영업 메모 업데이트
const updateSalesNote = async () => {
  if (!salesInfo.value || !hasPermission.value) return;
  
  try {
    await salesApi.updateSales(salesInfo.value.sales_idx, {
      sales_note: salesInfo.value.sales_note,
      org_code: currentUser.value.orgCode
    });
    snackbarText.value = '메모가 저장되었습니다.';
    snackbar.value = true;
  } catch (error) {
    console.error('영업 메모 업데이트 오류:', error);
    snackbarText.value = '메모 저장에 실패했습니다.';
    snackbar.value = true;
  }
};

// 관리자 여부 (대리로그인 시 대리로그인한 권한 기준)
const isAdmin = computed(() => (currentUser.value?.roleLevel || 0) >= 60);

// 로그 수정 시작
const startLogEdit = (log: any) => {
  editingLogIdx.value = log.log_idx;
  editingLogContent.value = log.log_content || '';
};

// 로그 수정 취소
const cancelLogEdit = () => {
  editingLogIdx.value = null;
  editingLogContent.value = '';
};

// 로그 수정 저장
const saveLogEdit = async (logIdx: number) => {
  if (!editingLogContent.value.trim()) return;
  try {
    await salesApi.updateSalesLog(logIdx, editingLogContent.value.trim());
    // 로컬 데이터 갱신
    const log = salesLogs.value.find((l: any) => l.log_idx === logIdx);
    if (log) log.log_content = editingLogContent.value.trim();
    cancelLogEdit();
    notifyParentSalesUpdate();
  } catch (error) {
    console.error('로그 수정 오류:', error);
  }
};

// 로그 삭제 (log_status='deleted'로 마킹 → 조회에서 제외)
const confirmDeleteLog = async (logIdx: number) => {
  if (!confirm('이 이력을 삭제하시겠습니까?')) return;
  try {
    await salesApi.deleteSalesLog(logIdx);
    // 로컬 데이터에서도 제거 (DB에는 남아있음)
    salesLogs.value = salesLogs.value.filter((l: any) => l.log_idx !== logIdx);
    notifyParentSalesUpdate();
  } catch (error) {
    console.error('로그 삭제 오류:', error);
  }
};

// 영업 정보 로드
const loadSalesInfo = async (ccNumber: string) => {
  try {
    const res = await salesApi.getSalesByFacility(ccNumber);
    salesInfo.value = res.data?.data?.sales || null;
    salesLogs.value = res.data?.data?.logs || [];
  } catch (error) {
    console.error('영업 정보 조회 오류:', error);
  }
};

// 데이터 로드
const loadDetail = async (ccNumber: string) => {
  loading.value = true;
  detail.value = null;
  detailTab.value = 'basic';
  
  try {
    const [detailRes, bizRes] = await Promise.all([
      crawlApi.getDetail(ccNumber),
      crawlApi.getBizGubunList()
    ]);
    
    detail.value = detailRes.data?.data || null;
    bizGubunMap.value = bizRes.data?.data || [];
    
    // embedded 모드가 아닐 때만 document.title 변경
    if (!props.embedded && detail.value?.main?.cc_name) {
      document.title = `${detail.value.main.cc_name} - 시설 상세정보`;
    }
    
    if (currentUser.value) {
      await loadSalesInfo(ccNumber);
    }
  } catch (error) {
    console.error('시설 상세 조회 오류:', error);
  } finally {
    loading.value = false;
  }
};

// props.ccNumber 변경 감지 (embedded 모드)
watch(() => props.ccNumber, (newVal) => {
  if (newVal && props.embedded) {
    loadDetail(newVal);
  }
}, { immediate: false });

// route.params.ccNumber 변경 감지 (일반 모드)
watch(() => route.params.ccNumber, (newVal) => {
  if (newVal && !props.embedded) {
    loadDetail(newVal as string);
  }
}, { immediate: false });

onMounted(async () => {
  await loadVisibleOrgCodes();
  
  // embedded 모드면 props에서, 아니면 route에서 ccNumber 가져옴
  const ccNumber = props.embedded ? props.ccNumber : (route.params.ccNumber as string);
  if (ccNumber) {
    loadDetail(ccNumber);
  } else {
    loading.value = false;
  }
});
</script>
